import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Ban, CalendarDays, CigaretteOff, Clock, FlameKindling, Loader2, PawPrint, ShieldCheck } from "lucide-react";
import { EASE } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  check_in: "",
  check_out: "",
  guests: "2",
  season: "July or August · Two-week minimum ($2,400)",
  message: "",
};

const NOTES = [
  { icon: Clock, text: "Replies within 24 hours, directly from the owner" },
  { icon: ShieldCheck, text: "No booking fees; you deal with us, not a platform" },
  { icon: CalendarDays, text: "Check in Monday 4pm · check out Sunday noon" },
];

const HOUSE_NOTES = [
  { icon: CigaretteOff, text: "Smoke-free cottage, no smoking inside" },
  { icon: PawPrint, text: "No pets during your stay" },
  { icon: FlameKindling, text: "Wood fireplace & firewood for chilly evenings" },
  { icon: Ban, text: "No open fires on the property or in the community" },
];

const inputCls =
  "w-full rounded-lg border border-[#1E3323]/15 bg-[#F7F4EF] px-4 py-3 text-sm text-[#1A1C19] outline-none transition-all duration-300 placeholder:text-[#767970]/60 focus:border-[#C8634B] focus:ring-2 focus:ring-[#C8634B]/20";

const labelCls =
  "mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-[#4A4D46]";

export default function EnquiryForm() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const onPrefill = (e) =>
      setForm((f) => ({
        ...f,
        check_in: e.detail.check_in,
        check_out: e.detail.check_out,
      }));
    window.addEventListener("prefill-dates", onPrefill);
    return () => window.removeEventListener("prefill-dates", onPrefill);
  }, []);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, guests: Number(form.guests) });
      toast.success("Enquiry received", {
        description: "Thank you. The owner will reply within 24 hours.",
      });
      setForm(EMPTY);
    } catch {
      toast.error("Could not send your enquiry", {
        description: "Please try again in a moment.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="enquire"
      data-testid="enquiry-form-section"
      className="mx-auto max-w-7xl px-6 py-24 sm:px-10 sm:py-36"
    >
      <div className="grid gap-16 lg:grid-cols-2 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <div className="mb-6 flex items-center gap-4">
            <span className="h-px w-12 bg-[#C8634B]" />
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#C8634B]">
              Reserve
            </p>
          </div>
          <h2 className="font-serif text-3xl leading-snug tracking-tight text-[#1E3323] sm:text-4xl lg:text-5xl">
            Summer 2027 starts{" "}
            <span className="italic text-[#C8634B]">with a note.</span>
          </h2>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-[#4A4D46] sm:text-base">
            Tell us when you'd like to come and who's coming with you. Your
            enquiry goes straight to our inbox. No middlemen, no booking
            platforms, just a conversation about your summer.
          </p>
          <ul className="mt-10 space-y-5">
            {NOTES.map((note) => (
              <li key={note.text} className="flex items-center gap-4">
                <span className="rounded-full border border-[#1E3323]/15 p-2.5 text-[#1E3323]">
                  <note.icon size={16} strokeWidth={1.75} />
                </span>
                <span className="text-sm text-[#4A4D46]">{note.text}</span>
              </li>
            ))}
          </ul>

          <div
            data-testid="house-notes"
            className="mt-10 rounded-xl border border-[#1E3323]/12 bg-[#EFECE6]/70 p-6"
          >
            <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.22em] text-[#C8634B]">
              House notes: good to know
            </p>
            <ul className="space-y-3">
              {HOUSE_NOTES.map((note) => (
                <li
                  key={note.text}
                  className="flex items-center gap-3 text-sm text-[#4A4D46]"
                >
                  <note.icon
                    size={15}
                    strokeWidth={1.75}
                    className="shrink-0 text-[#1E3323]"
                  />
                  {note.text}
                </li>
              ))}
            </ul>
          </div>
        </motion.div>

        <motion.form
          data-testid="enquiry-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
          className="rounded-2xl border border-[#1E3323]/12 bg-white p-8 shadow-xl shadow-[#1E3323]/5 sm:p-10"
        >
          <div className="grid gap-6 sm:grid-cols-2">
            <div className="sm:col-span-1">
              <label htmlFor="enquiry-name" className={labelCls}>
                Your name *
              </label>
              <input
                id="enquiry-name"
                data-testid="enquiry-form-input-name"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Jane Doe"
                className={inputCls}
              />
            </div>
            <div className="sm:col-span-1">
              <label htmlFor="enquiry-email" className={labelCls}>
                Email *
              </label>
              <input
                id="enquiry-email"
                data-testid="enquiry-form-input-email"
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="jane@example.com"
                className={inputCls}
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="enquiry-phone" className={labelCls}>
                Phone (optional)
              </label>
              <input
                id="enquiry-phone"
                data-testid="enquiry-form-input-phone"
                type="tel"
                value={form.phone}
                onChange={set("phone")}
                placeholder="(204) 555-0123"
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="enquiry-check-in" className={labelCls}>
                Check-in
              </label>
              <input
                id="enquiry-check-in"
                data-testid="enquiry-form-input-check-in"
                type="date"
                min="2027-05-03"
                max="2027-09-27"
                value={form.check_in}
                onChange={set("check_in")}
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="enquiry-check-out" className={labelCls}>
                Check-out
              </label>
              <input
                id="enquiry-check-out"
                data-testid="enquiry-form-input-check-out"
                type="date"
                min="2027-05-09"
                max="2027-10-03"
                value={form.check_out}
                onChange={set("check_out")}
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="enquiry-guests" className={labelCls}>
                Guests
              </label>
              <select
                id="enquiry-guests"
                data-testid="enquiry-form-select-guests"
                value={form.guests}
                onChange={set("guests")}
                className={inputCls}
              >
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <option key={n} value={n}>
                    {n} {n === 1 ? "guest" : "guests"}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="enquiry-season" className={labelCls}>
                Season
              </label>
              <select
                id="enquiry-season"
                data-testid="enquiry-form-select-season"
                value={form.season}
                onChange={set("season")}
                className={inputCls}
              >
                <option>July or August · Two-week minimum ($2,400)</option>
                <option>May, June, Sept or Oct · One week or more</option>
                <option>Flexible, surprise me</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="enquiry-message" className={labelCls}>
                Anything we should know?
              </label>
              <textarea
                id="enquiry-message"
                data-testid="enquiry-form-textarea-message"
                rows={4}
                value={form.message}
                onChange={set("message")}
                placeholder="Anniversaries, kids, early arrivals... tell us about your trip."
                className={`${inputCls} resize-none`}
              />
            </div>
          </div>

          <button
            data-testid="enquiry-form-submit-button"
            type="submit"
            disabled={sending}
            className="mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-[#1E3323] py-4 text-sm font-semibold uppercase tracking-[0.15em] text-[#F7F4EF] transition-all duration-300 hover:bg-[#C8634B] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {sending && <Loader2 size={16} className="animate-spin" />}
            {sending ? "Sending…" : "Send Enquiry"}
          </button>
          <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-[#767970]">
            Goes straight to the owner · No spam, ever
          </p>
        </motion.form>
      </div>
    </section>
  );
}
